/**
 * TextEditorModal Component
 * Floating draggable/resizable modal for editing text
 * Features:
 * - Draggable window (no overlay - can see canvas)
 * - Resizable from edges and corners
 * - Edit and Preview mode toggle (tabs)
 * - Keyboard shortcuts: Esc to cancel, Cmd/Ctrl+Enter to accept
 * - Improve and Condense AI actions with selection support
 * - Character count display
 * - Animated close-to-node transition
 */

import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import remarkBreaks from 'remark-breaks';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CompressIcon from '@mui/icons-material/Compress';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { SettingsFieldConfig } from './NodeSettingsFooter';
import { CreditsIcon } from './CreditsIcon';
import { useTutorial } from '../../contexts/TutorialContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import promptImprovementService from '../../services/PromptImprovementService';
import logger from '../../utils/logger';
import './TextEditorModal.css';

export interface PromptImprovementContext {
  contentType?: 'image' | 'video' | 'text';
  selectedModel?: string;
  upstreamContext?: string;
  imageContext?: string;
  /** Actual image URLs from connected nodes for visual analysis */
  imageUrls?: string[];
  /** Current node parameter values (duration, aspectRatio, resolution, quality, etc.) */
  nodeParameters?: Record<string, any>;
}

interface TextEditorModalProps {
  isOpen: boolean;
  value: string;
  onChange: (value: string) => void;
  onAccept: () => void;
  onCancel: () => void;
  // Animation origin (node position)
  originRect?: DOMRect | null;
  // AI features
  promptImprovementContext?: PromptImprovementContext;
  onCondenseRequest?: (text: string) => Promise<string>;
  // Generate feature (for nodes that can generate)
  canGenerate?: boolean;
  onAcceptAndGenerate?: (value: string) => void;
  // Fallback submit handler (called on Ctrl+Enter if onAcceptAndGenerate not available)
  onSubmit?: (value?: string) => void;
  // Optional
  placeholder?: string;
  title?: string;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple' | 'orange';
  // Output media type for styling border color
  mediaOutputType?: 'text' | 'image' | 'video';
  /** Opens the Iris floating panel */
  onOpenIris?: () => void;
  // Inline settings (rendered as compact toolbar)
  settingsFields?: SettingsFieldConfig[];
  settingsValues?: Record<string, any>;
  onSettingsChange?: (fieldId: string, value: any) => void;
  /** Credit cost shown on the Generate button */
  creditCost?: number;
  /** When true, a BYOK key covers this node's provider — show 🔑, zero credits */
  usesOwnKey?: boolean;
}

type ViewMode = 'edit' | 'preview';
type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface FloatingState {
  position: { x: number; y: number };
  size: { width: number; height: number };
  isDragging: boolean;
  isResizing: boolean;
  dragOffset: { x: number; y: number };
  resizeHandle: ResizeHandle | null;
}

interface DictationState {
  status: 'idle' | 'recording' | 'processing' | 'error';
  recordingStartTime: number | null;
  elapsedSeconds: number;
  mediaRecorder: MediaRecorder | null;
  audioChunks: Blob[];
  errorMessage: string | null;
}

const MIN_WIDTH = 400;
const MIN_HEIGHT = 300;
const MAX_WIDTH_PERCENT = 0.9;
const MAX_HEIGHT_PERCENT = 0.9;
const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 600;

/**
 * SettingsToolbar — compact inline settings for the editor modal.
 * - Boolean / 2-option fields → toggle button (cycles on click)
 * - 3+ option fields → button with dropdown
 */
interface SettingsToolbarProps {
  fields: SettingsFieldConfig[];
  values: Record<string, any>;
  onChange: (fieldId: string, value: any) => void;
}

const SettingsToolbar: React.FC<SettingsToolbarProps> = memo(({ fields, values, onChange }) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!openDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openDropdown]);

  // Evaluate showWhen condition
  const shouldShow = (field: SettingsFieldConfig): boolean => {
    if (field.hidden) return false;
    // Generation Mode is edited here just like in the node settings footer.
    // It writes to parameters.mode via the same onChange (handleSettingsChange),
    // which re-derives input handles (e.g. first-last-frame adds a 2nd image handle)
    // and re-resolves conditionalOptions (e.g. Veo duration) on the next render.
    if (!field.showWhen) return true;
    const { field: depField, value: condValue, operator = '=' } = field.showWhen;
    const fieldValue = values[depField];
    switch (operator) {
      case '>': return fieldValue > condValue!;
      case '<': return fieldValue < condValue!;
      case '>=': return fieldValue >= condValue!;
      case '<=': return fieldValue <= condValue!;
      case '!=': return fieldValue !== condValue;
      default: return fieldValue === condValue;
    }
  };

  // Resolve options (including conditional options and model fields)
  const getOptions = (field: SettingsFieldConfig): Array<{ value: string; label: string }> => {
    // Model type fields use 'models' array with { id, name }
    if (field.type === 'model' && field.models) {
      return field.models.map((m: any) => ({ value: m.id, label: m.name }));
    }
    if (field.conditionalOptions) {
      const depValue = String(values[field.conditionalOptions.dependsOn] || '');
      const conditionalOpts = field.conditionalOptions.rules[depValue];
      if (conditionalOpts) return conditionalOpts;
    }
    return field.options || [];
  };

  const visibleFields = fields.filter(shouldShow);

  return (
    <div className="settings-toolbar" ref={toolbarRef}>
      {visibleFields.map((field) => {
        const currentValue = values[field.id] ?? field.default;

        // Boolean/toggle field → toggle button
        if (field.type === 'boolean' || field.type === 'toggle') {
          const isOn = Boolean(currentValue);
          return (
            <button
              key={field.id}
              type="button"
              className={`settings-toolbar__btn ${isOn ? 'is-active' : ''}`}
              onClick={(e) => { e.stopPropagation(); onChange(field.id, !isOn); }}
              onMouseDown={(e) => e.stopPropagation()}
              title={field.label}
            >
              {isOn ? field.label : `No ${field.label}`}
            </button>
          );
        }

        // Number field → compact stepper with dropdown
        if (field.type === 'number' || field.type === 'slider') {
          const numValue = typeof currentValue === 'number' ? currentValue : (field.default as number) ?? 0;
          const min = field.min ?? 0;
          const max = field.max ?? Infinity;
          const step = field.step ?? 1;
          const isOpen = openDropdown === field.id;

          return (
            <div key={field.id} className="settings-toolbar__dropdown-wrapper">
              <button
                type="button"
                className={`settings-toolbar__btn settings-toolbar__btn--dropdown ${isOpen ? 'is-open' : ''}`}
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : field.id); }}
                onMouseDown={(e) => e.stopPropagation()}
                title={field.label}
              >
                {numValue}
                <KeyboardArrowDownIcon style={{ fontSize: 14, marginLeft: 2 }} />
              </button>
              {isOpen && (
                <div className="settings-toolbar__dropdown settings-toolbar__number-picker">
                  <div className="settings-toolbar__number-controls">
                    <button
                      type="button"
                      className="settings-toolbar__number-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newVal = Math.max(min, numValue - step);
                        onChange(field.id, newVal);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={numValue <= min}
                    >
                      −
                    </button>
                    <span className="settings-toolbar__number-value">{numValue}</span>
                    <button
                      type="button"
                      className="settings-toolbar__number-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newVal = Math.min(max, numValue + step);
                        onChange(field.id, newVal);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={numValue >= max}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        }

        // Select field
        const options = getOptions(field);
        const currentLabel = options.find(o => String(o.value) === String(currentValue))?.label || String(currentValue);

        // 2 options → cycle on click
        if (options.length === 2) {
          const nextValue = options.find(o => String(o.value) !== String(currentValue))?.value || options[0].value;
          return (
            <button
              key={field.id}
              type="button"
              className="settings-toolbar__btn"
              onClick={(e) => { e.stopPropagation(); onChange(field.id, nextValue); }}
              onMouseDown={(e) => e.stopPropagation()}
              title={`${field.label}: ${currentLabel}`}
            >
              {currentLabel}
            </button>
          );
        }

        // 3+ options → dropdown
        const isOpen = openDropdown === field.id;
        return (
          <div key={field.id} className="settings-toolbar__dropdown-wrapper">
            <button
              type="button"
              className={`settings-toolbar__btn settings-toolbar__btn--dropdown ${isOpen ? 'is-open' : ''}`}
              onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : field.id); }}
              onMouseDown={(e) => e.stopPropagation()}
              title={`${field.label}: ${currentLabel}`}
            >
              {currentLabel}
              <KeyboardArrowDownIcon style={{ fontSize: 14, marginLeft: 2 }} />
            </button>
            {isOpen && (
              <div className="settings-toolbar__dropdown">
                {options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`settings-toolbar__dropdown-item ${String(opt.value) === String(currentValue) ? 'is-selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(field.id, opt.value);
                      setOpenDropdown(null);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});
SettingsToolbar.displayName = 'SettingsToolbar';

const TextEditorModalComponent: React.FC<TextEditorModalProps> = ({
  isOpen,
  value,
  onChange,
  onCancel,
  originRect,
  promptImprovementContext,
  onCondenseRequest,
  canGenerate = false,
  onAcceptAndGenerate,
  onSubmit,
  placeholder = 'Enter text...',
  title = 'Edit Prompt',
  variant = 'neutral',
  mediaOutputType,
  onOpenIris,
  settingsFields,
  settingsValues,
  onSettingsChange,
  creditCost,
  usesOwnKey = false,
}) => {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<ViewMode>('edit');
  const [isImproving, setIsImproving] = useState(false);
  const [isCondensing, setIsCondensing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  
  // Local state for editing - committed to parent once on close (not on every keystroke)
  const [localValue, setLocalValue] = useState(value);
  // Ref so handleCloseWithAnimation can read the latest value without needing it as a dep
  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;
  
  // Floating modal state
  const [floatingState, setFloatingState] = useState<FloatingState>({
    position: { x: 0, y: 0 },
    size: { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
    isDragging: false,
    isResizing: false,
    dragOffset: { x: 0, y: 0 },
    resizeHandle: null,
  });
  
  // Dictation state
  const [dictationState, setDictationState] = useState<DictationState>({
    status: 'idle',
    recordingStartTime: null,
    elapsedSeconds: 0,
    mediaRecorder: null,
    audioChunks: [],
    errorMessage: null,
  });
  const [dictationLanguage, setDictationLanguage] = useState<'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'nl' | 'ru' | 'ja' | 'zh'>('en');
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Confirmation dialog state
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [closeConfirmationType, setCloseConfirmationType] = useState<'ai-operation' | 'dictation' | null>(null);
  const [closeConfirmationRemember, setCloseConfirmationRemember] = useState(false);
  const pendingCloseRef = useRef<boolean>(false);
  
  // LocalStorage keys for preferences
  const AI_OPERATION_CLOSE_PREF = 'textEditorModal.aiOperationClosePref'; // 'cancel' | 'background' | 'keep'
  const DICTATION_CLOSE_PREF = 'textEditorModal.dictationClosePref'; // 'keep' | 'discard'
  
  // Use ref for onChange to ensure it's always current even if modal closes
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  // Sync local value when modal opens or value prop changes from outside
  useEffect(() => {
    if (isOpen) {
      setLocalValue(value);
    }
  }, [isOpen, value]);

  // Consume tutorial prompt fill ("Use" button in step 6)
  const { state: tutorialState, clearPendingPromptFill } = useTutorial();
  useEffect(() => {
    if (isOpen && tutorialState.pendingPromptFill) {
      setLocalValue(tutorialState.pendingPromptFill);
      clearPendingPromptFill();
    }
  }, [isOpen, tutorialState.pendingPromptFill, clearPendingPromptFill]);
  
  // Cleanup dictation on unmount or modal close
  useEffect(() => {
    if (!isOpen) {
      // Stop recording if active
      if (dictationState.mediaRecorder && dictationState.status === 'recording') {
        dictationState.mediaRecorder.stop();
      }
      // Clear timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      // Reset state
      setDictationState({
        status: 'idle',
        recordingStartTime: null,
        elapsedSeconds: 0,
        mediaRecorder: null,
        audioChunks: [],
        errorMessage: null,
      });
    }
  }, [isOpen, dictationState.mediaRecorder, dictationState.status]);

  // Update local value only - DO NOT call onChange here.
  // Calling onChange (→ handlePromptChange → setNodes) on every keystroke forces a full
  // ReactFlow re-render of every node on the canvas for each character typed, which
  // causes extreme UI lag. Value is committed once to the node when the modal closes.
  const handleValueChange = useCallback((newValue: string) => {
    setLocalValue(newValue);
  }, []);

  // Initialize position centered when opening (full-screen on mobile)
  useEffect(() => {
    if (isOpen) {
      if (isMobile) {
        setFloatingState(prev => ({
          ...prev,
          position: { x: 0, y: 0 },
          size: { width: window.innerWidth, height: window.innerHeight },
        }));
      } else {
        const centerX = (window.innerWidth - DEFAULT_WIDTH) / 2;
        const centerY = (window.innerHeight - DEFAULT_HEIGHT) / 2;
        setFloatingState(prev => ({
          ...prev,
          position: { x: centerX, y: centerY },
          size: { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
        }));
      }
    }
  }, [isOpen, isMobile]);

  // Apply bounds to keep modal at least 50% visible
  const applyBounds = useCallback((x: number, y: number, width: number, height: number) => {
    const minX = -(width * 0.5);
    const maxX = window.innerWidth - (width * 0.5);
    const minY = -(height * 0.5);
    const maxY = window.innerHeight - (height * 0.5);

    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y)),
    };
  }, []);

  // Auto-focus textarea when opening or switching to edit mode
  useEffect(() => {
    if (isOpen && viewMode === 'edit' && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    }
  }, [isOpen, viewMode]);

  // ==== DRAG SYSTEM ====
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (isMobile) return; // No dragging on mobile (full-screen)
    // Don't drag from textarea, buttons, or preview
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'BUTTON' ||
      target.closest('.text-editor-textarea') ||
      target.closest('.text-editor-preview') ||
      target.closest('.header-action-button') ||
      target.closest('.resize-handle')
    ) {
      return;
    }

    setFloatingState(prev => ({
      ...prev,
      isDragging: true,
      dragOffset: {
        x: e.clientX - prev.position.x,
        y: e.clientY - prev.position.y,
      },
    }));

    e.preventDefault();
  }, []);

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!floatingState.isDragging) return;

    requestAnimationFrame(() => {
      const newX = e.clientX - floatingState.dragOffset.x;
      const newY = e.clientY - floatingState.dragOffset.y;

      const bounded = applyBounds(newX, newY, floatingState.size.width, floatingState.size.height);

      setFloatingState(prev => ({
        ...prev,
        position: bounded,
      }));
    });
  }, [floatingState.isDragging, floatingState.dragOffset, floatingState.size, applyBounds]);

  const handleDragEnd = useCallback(() => {
    setFloatingState(prev => ({
      ...prev,
      isDragging: false,
    }));
  }, []);

  // Attach drag listeners
  useEffect(() => {
    if (floatingState.isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
    return undefined;
  }, [floatingState.isDragging, handleDragMove, handleDragEnd]);

  // ==== RESIZE SYSTEM ====
  const handleResizeStart = useCallback((e: React.MouseEvent, handle: ResizeHandle) => {
    if (isMobile) return; // No resizing on mobile (full-screen)
    e.stopPropagation();
    
    setFloatingState(prev => ({
      ...prev,
      isResizing: true,
      resizeHandle: handle,
      dragOffset: {
        x: e.clientX,
        y: e.clientY,
      },
    }));

    e.preventDefault();
  }, []);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!floatingState.isResizing || !floatingState.resizeHandle) return;

    requestAnimationFrame(() => {
      const deltaX = e.clientX - floatingState.dragOffset.x;
      const deltaY = e.clientY - floatingState.dragOffset.y;
      
      const handle = floatingState.resizeHandle!; // Non-null assertion - checked above
      const { position, size } = floatingState;
      
      let newX = position.x;
      let newY = position.y;
      let newWidth = size.width;
      let newHeight = size.height;

      // Calculate new size based on handle
      if (handle.includes('e')) {
        newWidth = size.width + deltaX;
      }
      if (handle.includes('w')) {
        newWidth = size.width - deltaX;
        newX = position.x + deltaX;
      }
      if (handle.includes('s')) {
        newHeight = size.height + deltaY;
      }
      if (handle.includes('n')) {
        newHeight = size.height - deltaY;
        newY = position.y + deltaY;
      }

      // Apply size limits
      const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT;
      const maxHeight = window.innerHeight * MAX_HEIGHT_PERCENT;

      newWidth = Math.max(MIN_WIDTH, Math.min(maxWidth, newWidth));
      newHeight = Math.max(MIN_HEIGHT, Math.min(maxHeight, newHeight));

      // If size was clamped, adjust position back
      if (newWidth === MIN_WIDTH && handle.includes('w')) {
        newX = position.x + (size.width - MIN_WIDTH);
      }
      if (newHeight === MIN_HEIGHT && handle.includes('n')) {
        newY = position.y + (size.height - MIN_HEIGHT);
      }
      if (newWidth === maxWidth && handle.includes('w')) {
        newX = position.x + (size.width - maxWidth);
      }
      if (newHeight === maxHeight && handle.includes('n')) {
        newY = position.y + (size.height - maxHeight);
      }

      setFloatingState(prev => ({
        ...prev,
        position: { x: newX, y: newY },
        size: { width: newWidth, height: newHeight },
        dragOffset: {
          x: e.clientX,
          y: e.clientY,
        },
      }));
    });
  }, [floatingState.isResizing, floatingState.resizeHandle, floatingState.dragOffset, floatingState.position, floatingState.size]);

  const handleResizeEnd = useCallback(() => {
    setFloatingState(prev => ({
      ...prev,
      isResizing: false,
      resizeHandle: null,
    }));
  }, []);

  // Attach resize listeners
  useEffect(() => {
    if (floatingState.isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
    return undefined;
  }, [floatingState.isResizing, handleResizeMove, handleResizeEnd]);

  // Handle close with animation to node
  const handleCloseWithAnimation = useCallback(() => {
    // Commit the final edited value to the node once on close
    // (replaces the per-keystroke onChange calls that caused full ReactFlow re-renders)
    onChangeRef.current(localValueRef.current);

    if (!originRect || !modalRef.current) {
      // No origin, just close
      onCancel();
      return;
    }

    // Calculate target position (center of origin node)
    const targetX = originRect.left + originRect.width / 2;
    const targetY = originRect.top + originRect.height / 2;
    
    // Current position (center of modal)
    const currentX = floatingState.position.x + floatingState.size.width / 2;
    const currentY = floatingState.position.y + floatingState.size.height / 2;
    
    // Calculate delta
    const deltaX = targetX - currentX;
    const deltaY = targetY - currentY;

    // Set CSS variables for animation
    modalRef.current.style.setProperty('--close-translate-x', `${deltaX}px`);
    modalRef.current.style.setProperty('--close-translate-y', `${deltaY}px`);

    setIsClosing(true);
    
    setTimeout(() => {
      onCancel();
      setIsClosing(false);
    }, 300);
  }, [originRect, floatingState.position, floatingState.size, onCancel]);

  // Determine current operation name for confirmation dialog
  const getCurrentOperation = useCallback(() => {
    if (isImproving) return 'Improve Prompt';
    if (isCondensing) return 'Condense Text';
    return null;
  }, [isImproving, isCondensing]);
  
  // Check if AI operation or dictation is active
  const hasActiveOperation = useCallback(() => {
    const aiActive = isImproving || isCondensing;
    const dictationActive = dictationState.status === 'recording';
    return { aiActive, dictationActive, anyActive: aiActive || dictationActive };
  }, [isImproving, isCondensing, dictationState.status]);
  
  // Force close without confirmation
  const forceClose = useCallback(() => {
    // Stop dictation if recording
    if (dictationState.mediaRecorder && dictationState.status === 'recording') {
      dictationState.mediaRecorder.stop();
    }
    handleCloseWithAnimation();
  }, [dictationState.mediaRecorder, dictationState.status, handleCloseWithAnimation]);
  
  // Handle close with confirmation check
  const handleClose = useCallback(() => {
    const { aiActive, dictationActive } = hasActiveOperation();
    
    // Check for saved preferences
    if (aiActive) {
      const savedPref = localStorage.getItem(AI_OPERATION_CLOSE_PREF);
      if (savedPref) {
        // Use saved preference
        if (savedPref === 'cancel') {
          // Cancel the operation and close
          setIsImproving(false);
          setIsCondensing(false);
          forceClose();
        } else if (savedPref === 'background') {
          // Let it run in background, just close (result will update node)
          forceClose();
        } else if (savedPref === 'keep') {
          // Keep modal open, do nothing
          return;
        }
        return;
      }
      
      // Show confirmation dialog for AI operation
      setCloseConfirmationType('ai-operation');
      setShowCloseConfirmation(true);
      return;
    }
    
    if (dictationActive) {
      const savedPref = localStorage.getItem(DICTATION_CLOSE_PREF);
      if (savedPref) {
        // Use saved preference
        if (savedPref === 'discard') {
          // Stop recording and discard
          if (dictationState.mediaRecorder) {
            dictationState.mediaRecorder.stop();
          }
          forceClose();
        } else if (savedPref === 'keep') {
          // Stop recording and process transcription
          if (dictationState.mediaRecorder) {
            dictationState.mediaRecorder.stop();
          }
          // Modal will close after transcription completes
          pendingCloseRef.current = true;
        }
        return;
      }
      
      // Show confirmation dialog for dictation
      setCloseConfirmationType('dictation');
      setShowCloseConfirmation(true);
      return;
    }
    
    // No active operations, close normally
    handleCloseWithAnimation();
  }, [hasActiveOperation, forceClose, handleCloseWithAnimation, dictationState.mediaRecorder]);

  const { completeAction: tutorialCompleteAction } = useTutorial();

  // Handle generate with animation (value already synced, just trigger generation)
  const handleGenerate = useCallback(() => {
    if (!onAcceptAndGenerate && !onSubmit) return;
    
    tutorialCompleteAction('generate-start');

    // Close with animation first
    handleCloseWithAnimation();
    
    // Trigger generation after a small delay
    setTimeout(() => {
      if (onAcceptAndGenerate) {
        onAcceptAndGenerate(localValue);
      } else if (onSubmit) {
        onSubmit(localValue);
      }
    }, 50);
  }, [handleCloseWithAnimation, onAcceptAndGenerate, onSubmit, localValue, tutorialCompleteAction]);



  // Handle keyboard shortcuts (defined after callbacks so they're in scope)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Esc to close
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
      // Cmd/Ctrl + Enter to generate (if can generate)
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (canGenerate) {
          handleGenerate();
        } else {
          handleClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canGenerate, handleGenerate, handleClose]);

  // Handle wheel events to prevent zoom on scrollable content
  const handleWheel = useCallback((e: React.WheelEvent<HTMLTextAreaElement | HTMLDivElement>) => {
    const target = e.currentTarget;
    const isScrollable = target.scrollHeight > target.clientHeight;
    
    if (isScrollable) {
      // Stop propagation to prevent React Flow zoom
      e.stopPropagation();
    }
  }, []);

  // Get selection or all text
  const getSelectedOrAllText = useCallback((): { text: string; hasSelection: boolean } => {
    if (!textareaRef.current) {
      return { text: localValue, hasSelection: false };
    }

    const { selectionStart, selectionEnd } = textareaRef.current;
    const hasSelection = selectionStart !== selectionEnd;

    if (hasSelection) {
      return {
        text: localValue.substring(selectionStart, selectionEnd),
        hasSelection: true
      };
    }

    return { text: localValue, hasSelection: false };
  }, [localValue]);

  // Replace text (either selection or all) - syncs immediately to node
  const replaceText = useCallback((newText: string) => {
    if (!textareaRef.current) {
      // Modal might be closed, update node directly
      onChangeRef.current(newText);
      return;
    }

    const { selectionStart, selectionEnd } = textareaRef.current;
    const hasSelection = selectionStart !== selectionEnd;

    if (hasSelection) {
      // Replace selection
      const before = localValue.substring(0, selectionStart);
      const after = localValue.substring(selectionEnd);
      const fullNewValue = before + newText + after;
      handleValueChange(fullNewValue); // Sync to node immediately
      
      // Restore cursor position
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = selectionStart + newText.length;
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    } else {
      // Replace all
      handleValueChange(newText); // Sync to node immediately
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    }
  }, [localValue, handleValueChange]);

  // Handle improve
  const handleImprove = useCallback(async () => {
    const { text, hasSelection } = getSelectedOrAllText();
    
    if (!text.trim() || text.length < 10) {
      logger.warn('[TextEditorModal] Text too short to improve (min 10 chars)');
      return;
    }

    setIsImproving(true);
    try {
      const result = await promptImprovementService.improvePrompt({
        originalPrompt: text,
        contentType: promptImprovementContext?.contentType || 'text',
        selectedModel: promptImprovementContext?.selectedModel,
        upstreamContext: promptImprovementContext?.upstreamContext,
        imageContext: promptImprovementContext?.imageContext,
        imageUrls: promptImprovementContext?.imageUrls,
        nodeParameters: promptImprovementContext?.nodeParameters,
        // When improving a selection, pass the full text as surrounding context
        surroundingContext: hasSelection ? localValue : undefined
      });

      if (result.success) {
        replaceText(result.improvedPrompt);
        logger.info('[TextEditorModal] Prompt improved successfully');
      } else {
        logger.error('[TextEditorModal] Failed to improve prompt:', result.error);
      }
    } catch (error) {
      logger.error('[TextEditorModal] Error improving prompt:', error);
    } finally {
      setIsImproving(false);
    }
  }, [getSelectedOrAllText, promptImprovementContext, replaceText, localValue]);

  // Handle condense
  const handleCondense = useCallback(async () => {
    if (!onCondenseRequest) return;

    const { text } = getSelectedOrAllText();
    
    if (!text.trim() || text.length < 10) {
      logger.warn('[TextEditorModal] Text too short to condense (min 10 chars)');
      return;
    }

    setIsCondensing(true);
    try {
      const condensedText = await onCondenseRequest(text);
      replaceText(condensedText);
      logger.info('[TextEditorModal] Text condensed successfully');
    } catch (error) {
      logger.error('[TextEditorModal] Error condensing text:', error);
    } finally {
      setIsCondensing(false);
    }
  }, [getSelectedOrAllText, onCondenseRequest, replaceText]);

  // ==== DICTATION SYSTEM ====
  
  // Start recording
  const handleStartDictation = useCallback(async () => {
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const audioChunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Clear timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        
        // Process audio
        if (audioChunks.length > 0) {
          await handleTranscribeAudio(audioChunks);
        } else {
          setDictationState(prev => ({
            ...prev,
            status: 'idle',
            recordingStartTime: null,
            elapsedSeconds: 0,
            mediaRecorder: null,
            audioChunks: [],
          }));
        }
      };
      
      mediaRecorder.onerror = (event) => {
        logger.error('[TextEditorModal] MediaRecorder error:', event);
        setDictationState(prev => ({
          ...prev,
          status: 'error',
          errorMessage: 'Recording failed. Please try again.',
        }));
      };
      
      // Start recording
      mediaRecorder.start();
      
      // Update state
      setDictationState({
        status: 'recording',
        recordingStartTime: Date.now(),
        elapsedSeconds: 0,
        mediaRecorder,
        audioChunks: [],
        errorMessage: null,
      });
      
      // Start timer (updates every second)
      timerIntervalRef.current = setInterval(() => {
        setDictationState(prev => {
          if (prev.status !== 'recording' || !prev.recordingStartTime) return prev;
          
          const elapsed = Math.floor((Date.now() - prev.recordingStartTime) / 1000);
          
          // Auto-stop at 120 seconds
          if (elapsed >= 120) {
            if (prev.mediaRecorder && prev.mediaRecorder.state === 'recording') {
              prev.mediaRecorder.stop();
            }
            return prev;
          }
          
          return {
            ...prev,
            elapsedSeconds: elapsed,
          };
        });
      }, 1000);
      
    } catch (error) {
      logger.error('[TextEditorModal] Error starting dictation:', error);
      setDictationState(prev => ({
        ...prev,
        status: 'error',
        errorMessage: 'Could not access microphone. Please check permissions.',
      }));
    }
  }, []);
  
  // Stop recording
  const handleStopDictation = useCallback(() => {
    if (dictationState.mediaRecorder && dictationState.mediaRecorder.state === 'recording') {
      dictationState.mediaRecorder.stop();
    }
  }, [dictationState.mediaRecorder]);
  
  // Transcribe audio using Whisper API
  const handleTranscribeAudio = useCallback(async (audioChunks: Blob[]) => {
    setDictationState(prev => ({
      ...prev,
      status: 'processing',
    }));
    
    try {
      // Create audio blob
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm;codecs=opus' });
      
      // Create FormData
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', dictationLanguage);
      
      // Call Whisper API
      const response = await fetch('/api/openai/transcribe', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Transcription failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.text) {
        // Append transcribed text to CURRENT content (not the value from when recording started)
        // Use textarea ref value if available (even if empty string), otherwise fallback to localValue
        const currentValue = textareaRef.current ? textareaRef.current.value : localValue;
        const newValue = currentValue ? `${currentValue}\n\n${data.text}` : data.text;
        handleValueChange(newValue);
        
        logger.info('[TextEditorModal] Transcription successful');
        
        // Reset to idle
        setDictationState({
          status: 'idle',
          recordingStartTime: null,
          elapsedSeconds: 0,
          mediaRecorder: null,
          audioChunks: [],
          errorMessage: null,
        });
      } else {
        throw new Error('Transcription returned no text');
      }
      
    } catch (error) {
      logger.error('[TextEditorModal] Transcription error:', error);
      setDictationState(prev => ({
        ...prev,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Transcription failed',
      }));
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setDictationState({
          status: 'idle',
          recordingStartTime: null,
          elapsedSeconds: 0,
          mediaRecorder: null,
          audioChunks: [],
          errorMessage: null,
        });
      }, 3000);
    }
  }, [localValue, handleValueChange]);
  
  // Toggle dictation
  const handleToggleDictation = useCallback(() => {
    if (dictationState.status === 'idle') {
      handleStartDictation();
    } else if (dictationState.status === 'recording') {
      handleStopDictation();
    }
  }, [dictationState.status, handleStartDictation, handleStopDictation]);
  
  // Format timer display (MM:SS)
  const formatTimer = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // ==== CONFIRMATION DIALOG HANDLERS ====
  
  // Handle AI operation confirmation choice
  const handleAIOperationChoice = useCallback((choice: 'cancel' | 'background' | 'keep') => {
    // Save preference if checkbox is checked
    if (closeConfirmationRemember) {
      localStorage.setItem(AI_OPERATION_CLOSE_PREF, choice);
    }
    
    // Execute choice
    if (choice === 'cancel') {
      // Cancel the operation and close
      setIsImproving(false);
      setIsCondensing(false);
      forceClose();
    } else if (choice === 'background') {
      // Let it run in background, result will update node
      forceClose();
    } else if (choice === 'keep') {
      // Keep modal open, do nothing
    }
    
    // Close confirmation dialog
    setShowCloseConfirmation(false);
    setCloseConfirmationRemember(false);
  }, [closeConfirmationRemember, forceClose]);
  
  // Handle dictation confirmation choice
  const handleDictationChoice = useCallback((choice: 'keep' | 'discard') => {
    // Save preference if checkbox is checked
    if (closeConfirmationRemember) {
      localStorage.setItem(DICTATION_CLOSE_PREF, choice);
    }
    
    // Execute choice
    if (choice === 'discard') {
      // Stop recording and discard
      if (dictationState.mediaRecorder) {
        dictationState.mediaRecorder.stop();
      }
      forceClose();
    } else if (choice === 'keep') {
      // Stop recording and process transcription
      if (dictationState.mediaRecorder) {
        dictationState.mediaRecorder.stop();
      }
      // Modal will close after transcription completes
      pendingCloseRef.current = true;
    }
    
    // Close confirmation dialog
    setShowCloseConfirmation(false);
    setCloseConfirmationRemember(false);
  }, [closeConfirmationRemember, dictationState.mediaRecorder, forceClose]);
  
  // Auto-close after transcription if user chose "keep"
  useEffect(() => {
    if (pendingCloseRef.current && dictationState.status === 'idle') {
      pendingCloseRef.current = false;
      forceClose();
    }
  }, [dictationState.status, forceClose]);

  // Resize Handles Component
  const ResizeHandles: React.FC = () => {
    const handles: ResizeHandle[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
    
    return (
      <>
        {handles.map(handle => (
          <div
            key={handle}
            className={`resize-handle resize-handle-${handle}`}
            onMouseDown={(e) => handleResizeStart(e, handle)}
          />
        ))}
      </>
    );
  };

  if (!isOpen) return null;

  const modalContent = (
    // NO OVERLAY - directly the floating modal
    <div 
      ref={modalRef}
      className={`text-editor-modal floating ${isMobile ? 'mobile-fullscreen' : ''} ${isClosing ? 'closing' : ''} variant-${variant} ${
        floatingState.isDragging ? 'dragging' : ''
      } ${floatingState.isResizing ? 'resizing' : ''} ${mediaOutputType ? `media-${mediaOutputType}` : ''}`}
      onMouseDown={handleDragStart}
      style={{
        position: 'fixed',
        left: `${floatingState.position.x}px`,
        top: `${floatingState.position.y}px`,
        width: `${floatingState.size.width}px`,
        height: `${floatingState.size.height}px`,
        zIndex: 10001,
        cursor: floatingState.isDragging ? 'grabbing' : 'grab',
        '--close-translate-x': '0px',
        '--close-translate-y': '0px',
      } as React.CSSProperties}
    >
      {/* Inner content wrapper with padding between border and content */}
      <div className="text-editor-modal-inner">
        {/* Header */}
        <div className="text-editor-modal-header">
          <h3>{title}</h3>
          <div className="header-actions">
            <button 
              className="header-action-button improve-button"
              onClick={handleImprove}
              disabled={isImproving || isCondensing || localValue.trim().length < 10}
              title={isImproving ? "Improving..." : "Improve with AI (Cmd/Ctrl+I)"}
              type="button"
            >
              {isImproving ? (
                <div className="action-spinner" />
              ) : (
                <AutoFixHighIcon fontSize="small" />
              )}
            </button>

            {onCondenseRequest && (
              <button
                className="header-action-button condense-button"
                onClick={handleCondense}
                disabled={isCondensing || isImproving || localValue.trim().length < 10}
                title={isCondensing ? "Condensing..." : "Condense text"}
                type="button"
              >
                {isCondensing ? (
                  <div className="action-spinner" />
                ) : (
                  <CompressIcon fontSize="small" />
                )}
              </button>
            )}

            {/* Dictation Button */}
            <button
              className={`header-action-button dictation-button ${dictationState.status}`}
              onClick={handleToggleDictation}
              disabled={dictationState.status === 'processing'}
              title={
                dictationState.status === 'idle' ? 'Start voice dictation (2 min max)' :
                dictationState.status === 'recording' ? `Stop recording (${formatTimer(dictationState.elapsedSeconds)})` :
                dictationState.status === 'processing' ? 'Processing audio...' :
                dictationState.errorMessage || 'Error'
              }
              type="button"
            >
              {dictationState.status === 'processing' ? (
                <div className="action-spinner" />
              ) : dictationState.status === 'recording' ? (
                <>
                  <StopIcon fontSize="small" />
                  <span className="dictation-timer">{formatTimer(dictationState.elapsedSeconds)}</span>
                </>
              ) : (
                <MicIcon fontSize="small" />
              )}
            </button>

            <select
              className="dictation-language-select"
              value={dictationLanguage}
              onChange={(e) => setDictationLanguage(e.target.value as any)}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={dictationState.status !== 'idle'}
              title="Dictation language"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="it">Italian</option>
              <option value="pt">Portuguese</option>
              <option value="nl">Dutch</option>
              <option value="ru">Russian</option>
              <option value="ja">Japanese</option>
              <option value="zh">Chinese</option>
            </select>

            {/* Iris Button — hidden for now */}
            {false && onOpenIris && (
              <button
                className="iris-toolbar-btn"
                onClick={onOpenIris}
                title="Open Iris AI assistant"
                type="button"
              >
                <img src="/icons/iris-butterfly.svg" alt="Iris" style={{ width: 14, height: 14 }} />
                Iris
              </button>
            )}

            <button 
              className="close-button"
              onClick={handleClose}
              aria-label="Close"
              type="button"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="text-editor-modal-tabs">
          <button
            className={`tab-button ${viewMode === 'edit' ? 'active' : ''}`}
            onClick={() => setViewMode('edit')}
            type="button"
          >
            Edit
          </button>
          <button
            className={`tab-button ${viewMode === 'preview' ? 'active' : ''}`}
            onClick={() => setViewMode('preview')}
            type="button"
          >
            Preview
          </button>
        </div>

        {/* Content */}
        <div className="text-editor-modal-content">
          {viewMode === 'edit' ? (
            <textarea
              ref={textareaRef}
              value={localValue}
              onChange={(e) => handleValueChange(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onWheel={handleWheel}
              className="text-editor-textarea nodrag"
              placeholder={placeholder}
              spellCheck="true"
            />
          ) : (
            <div 
              className="text-editor-preview nodrag"
              onWheel={handleWheel}
            >
              {localValue ? (
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {localValue}
                </ReactMarkdown>
              ) : (
                <span className="placeholder-text">{placeholder}</span>
              )}
            </div>
          )}
        </div>

        {/* Settings Toolbar + Character Count */}
        <div className="text-editor-settings-bar">
          {settingsFields && settingsValues && onSettingsChange && (
            <SettingsToolbar
              fields={settingsFields}
              values={settingsValues}
              onChange={onSettingsChange}
            />
          )}
          <span className="text-editor-char-count-inline">
            {localValue.length} chars
          </span>
        </div>

        {/* Action Buttons */}
        <div className="text-editor-modal-actions">
          <div className="action-group left">
            <button
              type="button"
              className="close-button-text"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
          <div className="action-group right">
            {canGenerate && (onAcceptAndGenerate || onSubmit) && (
              <button
                type="button"
                className="generate-button"
                onClick={handleGenerate}
                title="Generate (Ctrl/Cmd+Enter)"
              >
                Generate
                {creditCost !== undefined && creditCost > 0 && (
                  usesOwnKey ? (
                    <span className="generate-credits generate-credits-byok" title="Runs on your own API key — no credits used">
                      <span role="img" aria-label="your key">🔑</span>
                      <span className="generate-credits-strike">{creditCost}</span>
                    </span>
                  ) : (
                    <span className="generate-credits">
                      <CreditsIcon size={12} />
                      <span>{creditCost}</span>
                    </span>
                  )
                )}
              </button>
            )}
          </div>
        </div>
        {/* End inner wrapper */}
      </div>

      {/* Resize Handles */}
      <ResizeHandles />
      
      {/* Confirmation Dialog */}
      {showCloseConfirmation && (
        <div className="text-editor-confirmation-overlay">
          <div className="text-editor-confirmation-dialog">
            {closeConfirmationType === 'ai-operation' && (
              <>
                <div className="confirmation-header">
                  <AutoFixHighIcon />
                  <h3>{getCurrentOperation()} in progress</h3>
                </div>
                <p className="confirmation-message">
                  An AI operation is currently running. What would you like to do?
                </p>
                <div className="confirmation-actions">
                  <button
                    className="confirmation-btn cancel"
                    onClick={() => handleAIOperationChoice('cancel')}
                  >
                    Close and Cancel
                  </button>
                  <button
                    className="confirmation-btn background"
                    onClick={() => handleAIOperationChoice('background')}
                  >
                    Close and Run in Background
                  </button>
                  <button
                    className="confirmation-btn keep"
                    onClick={() => handleAIOperationChoice('keep')}
                  >
                    Keep Open
                  </button>
                </div>
                <label className="confirmation-remember">
                  <input
                    type="checkbox"
                    checked={closeConfirmationRemember}
                    onChange={(e) => setCloseConfirmationRemember(e.target.checked)}
                  />
                  <span>Don't ask again and use this choice as default</span>
                </label>
              </>
            )}
            {closeConfirmationType === 'dictation' && (
              <>
                <div className="confirmation-header">
                  <MicIcon />
                  <h3>Voice Recording in Progress</h3>
                </div>
                <p className="confirmation-message">
                  You're currently recording. What would you like to do?
                </p>
                <div className="confirmation-actions">
                  <button
                    className="confirmation-btn discard"
                    onClick={() => handleDictationChoice('discard')}
                  >
                    Discard Recording
                  </button>
                  <button
                    className="confirmation-btn keep"
                    onClick={() => handleDictationChoice('keep')}
                  >
                    Keep and Transcribe
                  </button>
                </div>
                <label className="confirmation-remember">
                  <input
                    type="checkbox"
                    checked={closeConfirmationRemember}
                    onChange={(e) => setCloseConfirmationRemember(e.target.checked)}
                  />
                  <span>Don't ask again and use this choice as default</span>
                </label>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export const TextEditorModal = memo(TextEditorModalComponent);

export default TextEditorModal;
